import { lazy, Suspense } from 'react'
import { createHashRouter, Navigate, type RouteObject } from 'react-router-dom'
import { PosLayout } from '@/components/pos/PosLayout'
import { RouteLoading } from '@/components/common/RouteLoading'
import { RootErrorBoundary } from '@/components/common/RootErrorBoundary'

// POS pages
const PosLogin = lazy(() =>
  import('@/pages/pos/PosLogin').then((m) => ({ default: m.PosLogin }))
)
const RetailBilling = lazy(() =>
  import('@/pages/pos/RetailBilling').then((m) => ({ default: m.RetailBilling }))
)
const ChallanCreate = lazy(() =>
  import('@/pages/pos/ChallanCreate').then((m) => ({ default: m.ChallanCreate }))
)
const MonthEndBilling = lazy(() =>
  import('@/pages/pos/MonthEndBilling').then((m) => ({ default: m.MonthEndBilling }))
)
const CustomerManagement = lazy(() =>
  import('@/pages/pos/CustomerManagement').then((m) => ({ default: m.CustomerManagement }))
)
const InventoryView = lazy(() =>
  import('@/pages/pos/InventoryView').then((m) => ({ default: m.InventoryView }))
)
const ProductManagement = lazy(() =>
  import('@/pages/pos/ProductManagement').then((m) => ({ default: m.ProductManagement }))
)
const BulkEditProducts = lazy(() =>
  import('@/pages/pos/BulkEditProducts').then((m) => ({ default: m.BulkEditProducts }))
)
const BillingHistory = lazy(() =>
  import('@/pages/pos/BillingHistory').then((m) => ({ default: m.BillingHistory }))
)
const QuoteCreate = lazy(() =>
  import('@/pages/pos/QuoteCreate').then((m) => ({ default: m.QuoteCreate }))
)
const PaymentLinks = lazy(() =>
  import('@/pages/pos/PaymentLinks').then((m) => ({ default: m.PaymentLinks }))
)
const Transactions = lazy(() =>
  import('@/pages/pos/Transactions').then((m) => ({ default: m.Transactions }))
)
const PosSettings = lazy(() =>
  import('@/pages/pos/Settings').then((m) => ({ default: m.Settings }))
)
const ChequeRegister = lazy(() =>
  import('@/pages/pos/cheque').then((m) => ({ default: m.ChequeRegister }))
)
const ChequeNew = lazy(() =>
  import('@/pages/pos/cheque/New').then((m) => ({ default: m.NewCheque }))
)
const ChequeTemplates = lazy(() =>
  import('@/pages/pos/cheque/Templates').then((m) => ({ default: m.Templates }))
)
const ChequeTemplateEditor = lazy(() =>
  import('@/pages/pos/cheque/TemplateEditor').then((m) => ({ default: m.TemplateEditor }))
)
const ChequeCalibration = lazy(() =>
  import('@/pages/pos/cheque/CalibrationWizard').then((m) => ({ default: m.CalibrationWizard }))
)
const PurchaseInvoiceList = lazy(() =>
  import('@/pages/pos/purchases/PurchaseInvoiceList').then((m) => ({ default: m.PurchaseInvoiceList }))
)
const PurchaseInvoiceNew = lazy(() =>
  import('@/pages/pos/purchases/PurchaseInvoiceNew').then((m) => ({ default: m.PurchaseInvoiceNew }))
)
const PurchaseInvoiceDetail = lazy(() =>
  import('@/pages/pos/purchases/PurchaseInvoiceDetail').then((m) => ({ default: m.PurchaseInvoiceDetail }))
)
const SupplierManagement = lazy(() =>
  import('@/pages/pos/purchases/SupplierManagement').then((m) => ({ default: m.SupplierManagement }))
)

// Admin pages (POS-only, not on public web)
const AdminDashboard = lazy(() =>
  import('@/pages/AdminDashboard').then((m) => ({ default: m.AdminDashboard }))
)
const AdminDeliveries = lazy(() =>
  import('@/pages/AdminDeliveries').then((m) => ({ default: m.AdminDeliveries }))
)
const AdminOverview = lazy(() =>
  import('@/pages/admin/AdminOverview').then((m) => ({ default: m.AdminOverview }))
)
const AdminAgents = lazy(() =>
  import('@/pages/admin/AdminAgents').then((m) => ({ default: m.AdminAgents }))
)
const AdminOrders = lazy(() =>
  import('@/pages/admin/AdminOrders').then((m) => ({ default: m.AdminOrders }))
)
const AdminTransactions = lazy(() =>
  import('@/pages/admin/AdminTransactions').then((m) => ({ default: m.AdminTransactions }))
)
const AdminProducts = lazy(() =>
  import('@/pages/admin/AdminProducts').then((m) => ({ default: m.AdminProducts }))
)
const AdminCategories = lazy(() =>
  import('@/pages/admin/AdminCategories').then((m) => ({ default: m.AdminCategories }))
)
const AdminBrands = lazy(() =>
  import('@/pages/admin/AdminBrands').then((m) => ({ default: m.AdminBrands }))
)
const AdminFlashSales = lazy(() =>
  import('@/pages/admin/AdminFlashSales').then((m) => ({ default: m.AdminFlashSales }))
)
const AdminSeasonalSales = lazy(() =>
  import('@/pages/admin/AdminSeasonalSales').then((m) => ({ default: m.AdminSeasonalSales }))
)
const AdminBanners = lazy(() =>
  import('@/pages/admin/AdminBanners').then((m) => ({ default: m.AdminBanners }))
)
const AdminCoupons = lazy(() =>
  import('@/pages/admin/AdminCoupons').then((m) => ({ default: m.AdminCoupons }))
)
const AdminHomepageSections = lazy(() =>
  import('@/pages/admin/AdminHomepageSections').then((m) => ({ default: m.AdminHomepageSections }))
)
const AdminHomepageDesign = lazy(() =>
  import('@/pages/admin/AdminHomepageDesign').then((m) => ({ default: m.AdminHomepageDesign }))
)
const AdminSettings = lazy(() =>
  import('@/pages/admin/AdminSettings').then((m) => ({ default: m.AdminSettings }))
)
const AdminReturns = lazy(() =>
  import('@/pages/admin/AdminReturns').then((m) => ({ default: m.AdminReturns }))
)

const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<RouteLoading />}>
    <Component />
  </Suspense>
)

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/pos" replace />,
  },
  {
    path: '/pos/login',
    element: withSuspense(PosLogin),
    errorElement: <RootErrorBoundary />,
  },
  {
    path: '/pos',
    element: <PosLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: withSuspense(RetailBilling) },
      { path: 'challan', element: withSuspense(ChallanCreate) },
      { path: 'billing', element: withSuspense(MonthEndBilling) },
      { path: 'products', element: withSuspense(ProductManagement) },
      { path: 'products/bulk-edit', element: withSuspense(BulkEditProducts) },
      { path: 'customers', element: withSuspense(CustomerManagement) },
      { path: 'inventory', element: withSuspense(InventoryView) },
      { path: 'purchases', element: withSuspense(PurchaseInvoiceList) },
      { path: 'purchases/new', element: withSuspense(PurchaseInvoiceNew) },
      { path: 'purchases/suppliers', element: withSuspense(SupplierManagement) },
      { path: 'purchases/:id', element: withSuspense(PurchaseInvoiceDetail) },
      { path: 'purchases/:id/edit', element: withSuspense(PurchaseInvoiceNew) },
      { path: 'quotes', element: withSuspense(QuoteCreate) },
      { path: 'payment-links', element: withSuspense(PaymentLinks) },
      { path: 'transactions', element: withSuspense(Transactions) },
      { path: 'history', element: withSuspense(BillingHistory) },
      { path: 'settings', element: withSuspense(PosSettings) },
      { path: 'cheque', element: withSuspense(ChequeRegister) },
      { path: 'cheque/new', element: withSuspense(ChequeNew) },
      { path: 'cheque/templates', element: withSuspense(ChequeTemplates) },
      { path: 'cheque/templates/:id', element: withSuspense(ChequeTemplateEditor) },
      { path: 'cheque/calibrate', element: withSuspense(ChequeCalibration) },
    ],
  },
  {
    path: '/admin',
    element: withSuspense(AdminDashboard),
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: withSuspense(AdminOverview) },
      { path: 'deliveries', element: withSuspense(AdminDeliveries) },
      { path: 'agents', element: withSuspense(AdminAgents) },
      { path: 'orders', element: withSuspense(AdminOrders) },
      { path: 'transactions', element: withSuspense(AdminTransactions) },
      { path: 'products', element: withSuspense(AdminProducts) },
      { path: 'categories', element: withSuspense(AdminCategories) },
      { path: 'brands', element: withSuspense(AdminBrands) },
      { path: 'flash-sales', element: withSuspense(AdminFlashSales) },
      { path: 'seasonal-sales', element: withSuspense(AdminSeasonalSales) },
      { path: 'banners', element: withSuspense(AdminBanners) },
      { path: 'coupons', element: withSuspense(AdminCoupons) },
      { path: 'homepage', element: withSuspense(AdminHomepageSections) },
      { path: 'homepage-design', element: withSuspense(AdminHomepageDesign) },
      { path: 'settings', element: withSuspense(AdminSettings) },
      { path: 'returns', element: withSuspense(AdminReturns) },
    ],
  },
  {
    path: '*',
    element: <div className="flex items-center justify-center h-dvh text-gray-500">Page not found</div>,
  },
]

export { routes }

export const router = createHashRouter(routes)
